// Session lifecycle: events, token clearing, login completion, activity tracking and proactive refresh.

import {
  ACTIVITY_WINDOW_MS,
  OAUTH_CODE_VERIFIER,
  OAUTH_REFRESH_TOKEN,
  OAUTH_RETURN_TO,
  OAUTH_STATE,
  REFRESH_WINDOW_MS,
} from 'services/auth/constants';
import type { TokenResponse } from 'services/auth/token-endpoint';
import {
  clearMemoryToken,
  getAccessTokenExpiry,
  getValidAccessToken,
  releaseRefreshLatch,
  setMemoryToken,
  startStorageListener,
  stopStorageListener,
} from 'services/auth/token-store';

export type AuthEventName = 'unauthenticated' | 'token-changed' | 'degraded';
type AuthEventHandler = (payload?: unknown) => void;

const handlers: Record<AuthEventName, Set<AuthEventHandler>> = {
  unauthenticated: new Set(),
  'token-changed': new Set(),
  degraded: new Set(),
};

export const authEvents = {
  on: (name: AuthEventName, handler: AuthEventHandler): (() => void) => {
    handlers[name].add(handler);
    return () => { handlers[name].delete(handler); };
  },
  off: (name: AuthEventName, handler: AuthEventHandler): void => {
    handlers[name].delete(handler);
  },
  emit: (name: AuthEventName, payload?: unknown): void => {
    Array.from(handlers[name]).forEach((handler) => {
      try {
        handler(payload);
      } catch (error) {
        // One failing subscriber must not starve the others; surface it asynchronously.
        setTimeout(() => { throw error; }, 0);
      }
    });
  },
};

// Legacy keys are inlined on purpose so utils/constants can drop them.
const LEGACY_KEYS = ['access_token', 'token_claims', 'orderId', 'assistant-conversation'];
const SESSION_KEYS = [OAUTH_REFRESH_TOKEN, OAUTH_CODE_VERIFIER, OAUTH_STATE, OAUTH_RETURN_TO, ...LEGACY_KEYS];

// Bumped on every clear/login; a refresh that started under an older generation must not persist its result.
let generation = 0;

export const getSessionGeneration = (): number => generation;

let proactiveTimer: ReturnType<typeof setTimeout> | null = null;

const cancelProactiveTimer = (): void => {
  if (proactiveTimer !== null) clearTimeout(proactiveTimer);
  proactiveTimer = null;
};

// Removes the refresh token and the in-memory token. Keeps client_id and the PKCE keys.
export const clearTokens = (): void => {
  generation += 1;
  localStorage.removeItem(OAUTH_REFRESH_TOKEN);
  clearMemoryToken();
  cancelProactiveTimer();
};

// Full local sign-out state. No storage event fires in the same tab, so emit for same-tab consumers.
export const clearSession = (): void => {
  generation += 1;
  SESSION_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  clearMemoryToken();
  cancelProactiveTimer();
  authEvents.emit('unauthenticated');
};

// ---- Activity tracking ----

const ACTIVITY_THROTTLE_MS = 1_000;
const MAX_TIMER_MS = 2_147_483_647;
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart', 'wheel'];
const RESUME_EVENTS = ['focus', 'online', 'pageshow'];

let lastActivityAt = Date.now();

export const getLastActivityAt = (): number => lastActivityAt;

const recordActivity = (force = false): void => {
  const now = Date.now();
  if (force || now - lastActivityAt >= ACTIVITY_THROTTLE_MS) lastActivityAt = now;
};

const noop = () => undefined;

// A user-initiated trigger releases the refresh latch and runs exactly one refresh.
const resumeFromLatch = (): void => {
  if (releaseRefreshLatch()) getValidAccessToken().catch(noop);
};

const onActivity = (event: Event): void => {
  recordActivity();
  if (event.type === 'pointerdown' || event.type === 'keydown') resumeFromLatch();
};

const onVisibility = (): void => {
  if (document.visibilityState !== 'visible') return;
  recordActivity(true);
  resumeFromLatch();
};

let activityListening = false;

export const startActivityTracking = (): void => {
  if (activityListening) return;
  activityListening = true;
  ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, { capture: true, passive: true }));
  document.addEventListener('visibilitychange', onVisibility);
};

export const stopActivityTracking = (): void => {
  if (!activityListening) return;
  activityListening = false;
  ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity, { capture: true }));
  document.removeEventListener('visibilitychange', onVisibility);
};

// ---- Proactive refresh ----

const scheduleProactiveRefresh = (): void => {
  cancelProactiveTimer();
  const expiry = getAccessTokenExpiry();
  if (expiry === null) return;
  const wait = Math.min(Math.max(expiry - REFRESH_WINDOW_MS - Date.now(), 0), MAX_TIMER_MS);
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  proactiveTimer = setTimeout(runWindowCheck, wait);
};

// Refresh ahead of expiry only for an active tab; an idle tab refreshes on demand at its next interaction.
function runWindowCheck(): void {
  proactiveTimer = null;
  const expiry = getAccessTokenExpiry();
  if (expiry === null) return;
  if (Date.now() - lastActivityAt > ACTIVITY_WINDOW_MS) return;
  if (expiry - REFRESH_WINDOW_MS > Date.now()) {
    scheduleProactiveRefresh();
    return;
  }
  getValidAccessToken().catch(noop);
}

const onResume = (event: Event): void => {
  if (event.type === 'visibilitychange' && document.visibilityState !== 'visible') return;
  recordActivity(true);
  runWindowCheck();
};

let lifecycleStarted = false;
let unsubscribers: Array<() => void> = [];

export const startSessionLifecycle = (): void => {
  if (lifecycleStarted) return;
  lifecycleStarted = true;
  startActivityTracking();
  startStorageListener();
  RESUME_EVENTS.forEach((name) => window.addEventListener(name, onResume));
  document.addEventListener('visibilitychange', onResume);
  unsubscribers = [
    authEvents.on('token-changed', scheduleProactiveRefresh),
    authEvents.on('unauthenticated', cancelProactiveTimer),
  ];
  scheduleProactiveRefresh();
};

export const stopSessionLifecycle = (): void => {
  if (!lifecycleStarted) return;
  lifecycleStarted = false;
  stopActivityTracking();
  stopStorageListener();
  RESUME_EVENTS.forEach((name) => window.removeEventListener(name, onResume));
  document.removeEventListener('visibilitychange', onResume);
  unsubscribers.forEach((off) => off());
  unsubscribers = [];
  cancelProactiveTimer();
};

export const completeLogin = (tokenResponse: TokenResponse): void => {
  generation += 1;
  if (tokenResponse.refresh_token) localStorage.setItem(OAUTH_REFRESH_TOKEN, tokenResponse.refresh_token);
  setMemoryToken(tokenResponse.access_token, tokenResponse.expires_in);
  recordActivity(true);
  authEvents.emit('token-changed');
  startSessionLifecycle();
};
